import React, { useState, useEffect, useRef } from 'react';
import { Flex, Text, Tooltip, ClickableItem } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { useTranslation } from 'Components/Helpers/translate';
import { switchChat, createChat, loadChats, setSidebarExpanded } from '../Context/Helpers';
import { useAIChatDispatch, useAIChatSelector } from '../Context/AIChatContext';
import Conversation from './Conversation';
import UnreadBadge from './UnreadBadge';

import './Sidebar.scss';

/**
 * Custom hook to manage animation state with a delay
 * @param trigger - The boolean value that triggers the animation
 * @param delayMs - Delay in milliseconds before setting animation as done (default: 150)
 * @returns boolean indicating if the animation is done
 */
export const useAnimationDone = (trigger: boolean, delayMs: number = 150): boolean => {
	const [isAnimationDone, setIsAnimationDone] = useState(trigger);
	const isFirstRenderRef = useRef(true);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	if (isFirstRenderRef.current) {
		isFirstRenderRef.current = false;
		if (trigger && !isAnimationDone) setIsAnimationDone(true);
	} else {
		// On prop change
		if (trigger && !isAnimationDone) {
			if (timerRef.current) clearTimeout(timerRef.current);
			timerRef.current = setTimeout(() => {
				setIsAnimationDone(true);
			}, delayMs);
		}
		if (!trigger && isAnimationDone) {
			if (timerRef.current) clearTimeout(timerRef.current);
			setIsAnimationDone(false);
		}
	}

	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);

	return isAnimationDone;
};

interface SideBarProps {
	setShowStartMenu: (show: boolean) => void;
	showStartMenu: boolean;
	setStartMenuResetKey: React.Dispatch<React.SetStateAction<number>>;
}

const SideBar: React.FC<SideBarProps> = ({ setShowStartMenu, showStartMenu, setStartMenuResetKey }) => {
	const classes = new BemClass('AIChat');
	const { t } = useTranslation();
	const dispatch = useAIChatDispatch();
	const { chats, currentChatId, messages, loading, isChatSidebarExpanded } = useAIChatSelector(
		({ chats, currentChatId, messages, loading, isChatSidebarExpanded }) => ({
			chats,
			currentChatId,
			messages,
			loading,
			isChatSidebarExpanded
		})
	);

	const isExpanded = isChatSidebarExpanded;

	const showTitle = useAnimationDone(isExpanded);

	const validChats = chats.filter(c => c && c.id && (c.updatedAt || c.createdAt));

	const chatsWithUnread = validChats
		.filter(c => (c.unreadCount || 0) > 0)
		.sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
	const chatsWithoutUnread = validChats
		.filter(c => !c.unreadCount || c.unreadCount === 0)
		.sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));

	const groupedByDay: Record<string, typeof chatsWithoutUnread> = {};
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	chatsWithoutUnread.forEach(chat => {
		const timestamp = chat.updatedAt || chat.createdAt || Date.now();
		const chatDate = new Date(timestamp);

		if (isNaN(chatDate.getTime())) {
			console.warn('Invalid date for chat:', chat);
			return;
		}

		const chatDay = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());
		let dayKey: string;

		if (chatDay.getTime() === today.getTime()) {
			dayKey = t('aiChat.today', 'Today');
		} else if (chatDay.getTime() === yesterday.getTime()) {
			dayKey = t('aiChat.yesterday', 'Yesterday');
		} else {
			try {
				dayKey = chatDate.toLocaleDateString(undefined, {
					year: 'numeric',
					month: 'short',
					day: 'numeric'
				});
			} catch {
				dayKey = chatDate.toLocaleDateString();
			}
		}

		if (!groupedByDay[dayKey]) {
			groupedByDay[dayKey] = [];
		}
		groupedByDay[dayKey].push(chat);
	});

	// Load chats when sidebar expands
	useEffect(() => {
		if (isExpanded) {
			dispatch(loadChats());
		}
	}, [isExpanded]);

	const isLoading = currentChatId ? loading[currentChatId] : false;

	return (
		<div className={classes.elem('chatSidebar').mod({ expanded: isExpanded, collapsed: !isExpanded }).b()}>
			<Flex direction="column" className={classes.elem('chatSidebarContent').b()}>
				<Flex direction="column" className={classes.elem('chatSidebarHeader').b()}>
					<Tooltip
						title={isExpanded ? '' : t('aiChat.openSidebar', 'Open sidebar')}
						position="right"
						className={classes.elem('chatSidebarHeader').elem('tooltip').b()}
					>
						<ClickableItem
							icon="dock-to-right"
							onClick={e => {
								e.stopPropagation();
								dispatch(setSidebarExpanded(!isExpanded));
							}}
							title={showTitle ? t('aiChat.closeSidebar', 'Close sidebar') : undefined}
							borderRadius
						/>
					</Tooltip>

					<Tooltip
						title={isExpanded ? '' : t('aiChat.newChat', 'New chat')}
						position="right"
						className={classes.elem('chatSidebarHeader').elem('tooltip').b()}
					>
						<ClickableItem
							icon="pencil-square-o"
							title={showTitle ? t('aiChat.newChat', 'New chat') : undefined}
							disabled={currentChatId !== null && messages.length === 0}
							onClick={e => {
								e.stopPropagation();
								if (currentChatId === null || messages.length > 0) {
									dispatch(createChat());
								}
							}}
							borderRadius
						/>
					</Tooltip>

					<Tooltip
						title={isExpanded ? '' : t('aiChat.menu', 'Suggestions')}
						position="right"
						className={classes.elem('chatSidebarHeader').elem('tooltip').b()}
					>
						<ClickableItem
							icon="bars"
							title={showTitle ? t('aiChat.menu', 'Suggestions') : undefined}
							disabled={isLoading}
							onClick={e => {
								e.stopPropagation();
								if (!isLoading) {
									if (!showStartMenu) {
										setStartMenuResetKey(prev => prev + 1);
									}
									setShowStartMenu(!showStartMenu);
								}
							}}
							borderRadius
						/>
					</Tooltip>
				</Flex>
				{!isExpanded && chatsWithUnread.length > 0 && (
					<Tooltip title={t('aiChat.unreadMessages', 'Unread messages')} position="right">
						<UnreadBadge
							spacing="mt3"
							count={chatsWithUnread.length}
							onClick={e => {
								e.stopPropagation();
								dispatch(setSidebarExpanded(true));
							}}
							size="lg"
						/>
					</Tooltip>
				)}
				<div
					className={classes
						.elem('chatSidebarScrollable')
						.mod({ expanded: isExpanded, collapsed: !isExpanded })
						.b()}
					onWheel={e => {
						e.stopPropagation();
					}}
					onTouchMove={e => {
						e.stopPropagation();
					}}
				>
					<Flex direction="column" gap="u2">
						{validChats.length === 0 ? (
							<Text size="sm" color="grey-7" spacing="pl4">
								{t('aiChat.noChats', 'No chats yet')}
							</Text>
						) : (
							<>
								{chatsWithUnread.length > 0 && (
									<Flex direction="column">
										<Text bold spacing="ml4">
											{t('aiChat.unread', 'Unread')}
										</Text>
										{chatsWithUnread.map(chat => (
											<Conversation
												key={chat.id}
												chat={chat}
												onClick={() => {
													setShowStartMenu(false);
													dispatch(switchChat(chat.id));
												}}
											/>
										))}
									</Flex>
								)}
								{Object.entries(groupedByDay).map(([dayKey, dayChats]) => (
									<Flex key={dayKey} direction="column">
										<Text bold spacing="ml4 mt2 mb2">
											{dayKey}
										</Text>
										<Flex direction="column" gap="u2">
											{dayChats.map(chat => (
												<Conversation
													key={chat.id}
													chat={chat}
													onClick={() => {
														setShowStartMenu(false);
														dispatch(switchChat(chat.id));
													}}
												/>
											))}
										</Flex>
									</Flex>
								))}
							</>
						)}
					</Flex>
				</div>
			</Flex>
		</div>
	);
};

export default SideBar;
